import { toolCallFailure } from '@erp/json-renderer/apps';

/**
 * Turn refreshed feed keys into a state-store patch: each feed's value lives
 * at `/<key>`, which is what the view's `{ "$state": "/<key>" }` reads.
 * A key whose refresh failed keeps the value the view already has.
 */
export function appStatePatch(
  state: Record<string, unknown>,
  errors: ReadonlyArray<{ key: string }> = [],
): Record<string, unknown> {
  const failed = new Set(errors.map((error) => error.key));
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (!failed.has(key)) patch[`/${key}`] = value;
  }
  return patch;
}

/** A feed's status, at `/$data/<key>`. */
export interface FeedStatus {
  loading: boolean;
  error: string | null;
}

/**
 * The status patch for every feed a load or refresh settled: `/$data/<key>`
 * is `{ loading: false, error }`, the error being the failure's message.
 */
export function feedStatusPatch(
  state: Record<string, unknown>,
  errors: ReadonlyArray<{ key: string; message: string }> = [],
): Record<string, FeedStatus> {
  const patch: Record<string, FeedStatus> = {};
  for (const key of Object.keys(state)) {
    patch[`/$data/${key}`] = { loading: false, error: null };
  }
  for (const { key, message } of errors) {
    patch[`/$data/${key}`] = { loading: false, error: message };
  }
  return patch;
}

/** A command's status, at `/commands/<name>`. */
export interface CommandStatus {
  running: boolean;
  result: unknown;
  error: string | null;
}

export function commandStatusPatch(
  name: string,
  status: CommandStatus,
): Record<string, CommandStatus> {
  return { [`/commands/${name}`]: status };
}

/**
 * The feed errors still standing after a command refreshed some keys: a
 * refreshed key's old error is replaced by its new outcome, so a key that
 * loaded this time no longer shows as failed.
 */
export function settledFeedErrors<E extends { key: string }>(
  current: ReadonlyArray<E>,
  state: Record<string, unknown>,
  errors: ReadonlyArray<E>,
): E[] {
  const refreshed = new Set([
    ...Object.keys(state),
    ...errors.map((error) => error.key),
  ]);
  return [...current.filter((error) => !refreshed.has(error.key)), ...errors];
}

/**
 * A command's status once its tool returned. Many tools report a failure in
 * their result instead of throwing; that counts as the command failing.
 */
export function settledCommandStatus(result: unknown): CommandStatus {
  return { running: false, result, error: toolCallFailure(result) };
}
